import React from "react";
import PropTypes from "prop-types";

import NoDataPreview from "@/components/shared/noDataPreview";
import {
    filterItemsForUniversityMatch,
    filterUniversityRecordsBySelection,
    fetchAllInternalFairUniversityStudents,
    fetchInternalFairUniversitiesDirectory,
    getUniversityFilterOptions,
    getUniversityRowKey,
} from "./utils";

import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    CircularProgress,
    TextField,
    Typography,
    makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import RefreshIcon from "@material-ui/icons/Refresh";

const useStyles = makeStyles((theme) => ({
    actionsRow: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    filterField: {
        minWidth: 320,
        maxWidth: 620,
        background: theme.palette.common.white,
        [theme.breakpoints.down("sm")]: {
            minWidth: 0,
            maxWidth: "100%",
            width: "100%",
        },
    },
    accordion: {
        marginBottom: theme.spacing(1.5),
        boxShadow: "none",
        border: "1px solid #e0e0e0",
        "&:before": {
            display: "none",
        },
    },
    accordionSummary: {
        background: "#fafafa",
    },
    summaryTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(0.5),
    },
    summaryMeta: {
        color: "#666",
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1.5),
    },
    sectionActions: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
        gap: theme.spacing(1),
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            alignItems: "stretch",
        },
    },
    errorText: {
        color: theme.palette.error.main,
    },
    studentCard: {
        border: "1px solid #d9e3ea",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1.25, 1.5),
        background: "linear-gradient(180deg, #ffffff 0%, #fbfcfd 100%)",
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        "& + &": {
            marginTop: theme.spacing(1),
        },
    },
    studentRow: {
        display: "grid",
        gridTemplateColumns: "minmax(180px, 260px) minmax(0, 1fr)",
        gap: theme.spacing(1.5),
        alignItems: "center",
        [theme.breakpoints.down("sm")]: {
            gridTemplateColumns: "1fr",
            alignItems: "flex-start",
        },
    },
    studentIdentity: {
        minWidth: 0,
    },
    studentName: {
        fontWeight: 600,
        wordBreak: "break-word",
        color: "#183247",
        fontSize: "18px",
        lineHeight: 1.3,
    },
    studentMeta: {
        color: "#667b8a",
        marginTop: theme.spacing(0.2),
        wordBreak: "break-word",
        fontSize: "13px",
        lineHeight: 1.4,
    },
    notesCell: {
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(1),
        background: "#f8fbfc",
        borderRadius: theme.spacing(0.75),
        padding: theme.spacing(0.9, 1.1),
        border: "1px solid #e8eef2",
        [theme.breakpoints.down("sm")]: {
            alignItems: "flex-start",
        },
    },
    notesLabel: {
        fontWeight: 600,
        color: "#557086",
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        fontSize: "11px",
        flexShrink: 0,
        lineHeight: 1.4,
    },
    notesText: {
        color: "#5f7180",
        fontSize: "13px",
        lineHeight: 1.55,
        fontWeight: 400,
        wordBreak: "break-word",
        minWidth: 0,
    },
    warningNotesState: {
        color: "#c62828",
        fontSize: "13px",
        lineHeight: 1.5,
        fontWeight: 500,
    },
}));

const getDisplayValue = (value) => (value === "" ? "-" : value);
const getCommaSeparatedNotes = (notes) =>
    (Array.isArray(notes) ? notes : [])
        .flatMap((note) => (Array.isArray(note.comments) ? note.comments : []))
        .map((comment) => String(comment || "").trim())
        .filter(Boolean)
        .join(", ");

export default function UniversityWiseTab({ fairId, jsonUrl }) {
    const classes = useStyles();

    const [directoryLoading, setDirectoryLoading] = React.useState(true);
    const [directoryError, setDirectoryError] = React.useState("");
    const [universities, setUniversities] = React.useState([]);
    const [expandedPanel, setExpandedPanel] = React.useState(false);
    const [accordionStates, setAccordionStates] = React.useState({});
    const [selectedUniversities, setSelectedUniversities] = React.useState([]);

    const loadDirectory = React.useCallback(() => {
        setDirectoryLoading(true);
        setDirectoryError("");
        setExpandedPanel(false);
        setAccordionStates({});
        setSelectedUniversities([]);

        return fetchInternalFairUniversitiesDirectory({ jsonUrl })
            .then((data) => {
                setUniversities(data);
            })
            .catch(() => {
                setUniversities([]);
                setDirectoryError(
                    jsonUrl
                        ? "Unable to load the university directory right now."
                        : "University directory URL is not configured, so fallback data is being used."
                );
            })
            .finally(() => setDirectoryLoading(false));
    }, [jsonUrl]);

    React.useEffect(() => {
        loadDirectory();
    }, [loadDirectory]);

    const universityFilterOptions = React.useMemo(
        () => getUniversityFilterOptions(universities),
        [universities]
    );
    const filteredUniversities = React.useMemo(
        () => filterUniversityRecordsBySelection(universities, selectedUniversities),
        [universities, selectedUniversities]
    );

    React.useEffect(() => {
        if (!expandedPanel) return;
        const expandedStillVisible = filteredUniversities.some((row) => getUniversityRowKey(row) === expandedPanel);
        if (!expandedStillVisible) {
            setExpandedPanel(false);
        }
    }, [expandedPanel, filteredUniversities]);

    const loadStudentsForUniversity = React.useCallback(
        (row, forceRefresh = false) => {
            const rowKey = getUniversityRowKey(row);
            const existingState = accordionStates[rowKey];
            if (existingState && existingState.loaded && !forceRefresh) {
                return Promise.resolve();
            }

            setAccordionStates((prev) => ({
                ...prev,
                [rowKey]: {
                    ...(prev[rowKey] || {}),
                    loaded: false,
                    loading: true,
                    error: "",
                },
            }));

            return fetchAllInternalFairUniversityStudents({
                fairId,
                universityName: row.university_name,
                universityCrmId: row.crm_id,
            })
                .then((result) => {
                    setAccordionStates((prev) => {
                        const nextState = {
                            ...prev,
                            [rowKey]: {
                                loaded: true,
                                loading: false,
                                error: "",
                                data: result.data,
                                total: result.total,
                                totalStudents: result.totalStudents,
                            },
                        };
                        return nextState;
                    });
                })
                .catch(() => {
                    setAccordionStates((prev) => ({
                        ...prev,
                        [rowKey]: {
                            ...(prev[rowKey] || {}),
                            loaded: false,
                            loading: false,
                            error: "Unable to load students for this university.",
                            data: [],
                            total: 0,
                            totalStudents: 0,
                        },
                    }));
                });
        },
        [accordionStates, fairId]
    );

    const handleAccordionChange = (row) => (_, isExpanded) => {
        const rowKey = getUniversityRowKey(row);
        setExpandedPanel(isExpanded ? rowKey : false);
        if (isExpanded) {
            loadStudentsForUniversity(row);
        }
    };

    const renderStudentsState = (row) => {
        const rowKey = getUniversityRowKey(row);
        const state = accordionStates[rowKey];

        if (!state || state.loading) {
            return (
                <>
                    <Typography variant="body2" gutterBottom>
                        Loading students...
                    </Typography>
                    <CircularProgress size={24} color="primary" />
                </>
            );
        }

        if (state.error) {
            return (
                <Box>
                    <Typography variant="body2" className={classes.errorText} gutterBottom>
                        {state.error}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => loadStudentsForUniversity(row, true)}
                        startIcon={<RefreshIcon />}
                    >
                        Retry
                    </Button>
                </Box>
            );
        }

        return (
            <>
                <Box className={classes.sectionActions}>
                    <Typography variant="body2">
                        {state.data.length ? `${state.data.length} student records` : "No student records found"}
                    </Typography>
                    <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => loadStudentsForUniversity(row, true)}
                        startIcon={<RefreshIcon />}
                    >
                        Refresh Students
                    </Button>
                </Box>
                {state.data.length === 0 ? (
                    <NoDataPreview title="No students found" />
                ) : (
                    state.data.map((student) => {
                        const studentNotesForUniversity = filterItemsForUniversityMatch({
                            items: student.formattedNotes,
                            rowUniversityName: row.university_name,
                            rowUniversityCrmId: row.crm_id,
                            studentAttendedUniversities: student.attendedUniversities,
                            getItemUniversityName: (note) => note.universityName,
                        });
                        const missingAttendedUniversitiesForUniversity = filterItemsForUniversityMatch({
                            items: student.missingAttendedUniversities,
                            rowUniversityName: row.university_name,
                            rowUniversityCrmId: row.crm_id,
                            studentAttendedUniversities: student.attendedUniversities,
                            getItemUniversityName: (item) => item.universityName,
                            getItemUniversityCrmId: (item) => item.universityCrmId || item.university_crm_id || item.crmId,
                        });
                        const hasVisibleNotes =
                            studentNotesForUniversity.length > 0 ||
                            missingAttendedUniversitiesForUniversity.length > 0;
                        const combinedNotes = getCommaSeparatedNotes(studentNotesForUniversity);
                        const notesText =
                            combinedNotes ||
                            (missingAttendedUniversitiesForUniversity.length > 0
                                ? "Student attended but notes not added"
                                : "-");

                        return (
                            <Box key={student.registrantCode} className={classes.studentCard}>
                                <Box className={classes.studentRow}>
                                    <Box className={classes.studentIdentity}>
                                        <Typography variant="h3" className={classes.studentName}>
                                            {getDisplayValue(student.name)}
                                        </Typography>
                                        <Typography variant="body2" className={classes.studentMeta}>
                                            Unique ID: {getDisplayValue(student.registrantCode)}
                                        </Typography>
                                    </Box>
                                    {hasVisibleNotes ? (
                                        <Box className={classes.notesCell}>
                                            <Typography variant="body2" className={classes.notesLabel}>
                                                Notes
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                className={
                                                    missingAttendedUniversitiesForUniversity.length > 0 && !combinedNotes
                                                        ? classes.warningNotesState
                                                        : classes.notesText
                                                }
                                            >
                                                {notesText}
                                            </Typography>
                                        </Box>
                                    ) : null}
                                </Box>
                            </Box>
                        );
                    })
                )}
            </>
        );
    };

    if (directoryLoading) {
        return (
            <Box textAlign="center" py={4}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    if (!directoryLoading && universities.length === 0) {
        return <NoDataPreview title={directoryError || "No university records found"} />;
    }

    return (
        <>
            <Box className={classes.actionsRow}>
                <Autocomplete
                    multiple
                    options={universityFilterOptions}
                    value={selectedUniversities}
                    onChange={(_, value) => setSelectedUniversities(value)}
                    disableCloseOnSelect
                    className={classes.filterField}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            variant="outlined"
                            label="Filter Universities"
                            placeholder={selectedUniversities.length === 0 ? "Select universities" : ""}
                        />
                    )}
                />
                <Button variant="outlined" color="primary" onClick={loadDirectory} startIcon={<RefreshIcon />}>
                    Refresh University List
                </Button>
            </Box>

            {directoryError && jsonUrl && (
                <Typography variant="body2" className={classes.errorText} gutterBottom>
                    {directoryError}
                </Typography>
            )}

            {filteredUniversities.map((row) => {
                const rowKey = getUniversityRowKey(row);
                return (
                    <Accordion
                        key={rowKey}
                        expanded={expandedPanel === rowKey}
                        onChange={handleAccordionChange(row)}
                        className={classes.accordion}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                            <Box width="100%">
                                <Typography variant="subtitle1" className={classes.summaryTitle}>
                                    {row.university_name}
                                </Typography>
                                <Box className={classes.summaryMeta}>
                                    <Typography variant="body2">Country: {getDisplayValue(row.country)}</Typography>
                                    <Typography variant="body2">Desk: {getDisplayValue(row.desk_number)}</Typography>
                                    <Typography variant="body2">Staff: {getDisplayValue(row.staff)}</Typography>
                                </Box>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Box width="100%">{renderStudentsState(row)}</Box>
                        </AccordionDetails>
                    </Accordion>
                );
            })}

            {!directoryLoading && filteredUniversities.length === 0 ? (
                <NoDataPreview title="No university records found for the selected filters" />
            ) : null}
        </>
    );
}

UniversityWiseTab.propTypes = {
    fairId: PropTypes.string.isRequired,
    jsonUrl: PropTypes.string,
};
